import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAppSelector } from 'store';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import dataService from 'services/dataService';
import {
  API_READINGS_BULK_COMMENT_GENERATE,
  API_READINGS_BULK_COMMENT_SUBMIT,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import common from 'constants/common';
import useUser from 'utils/useUser';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import type {
  ValuesBulkCommentGenerate,
  ValuesBulkCommentSubmit,
} from './form/validation-schema';

interface IProps {
  patientGuid: string;
  onClose: () => void;
  activityCount: number;
}

export const useLogic = ({ patientGuid, onClose, activityCount }: IProps) => {
  const [generatedComment, setGeneratedComment] = useState<string>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [commentLoading, setCommentLoading] = useState<boolean>(false);
  const [reviewLoading, setReviewLoading] = useState<boolean>(false);

  const [startTime, endTime] = useAppSelector<[Date, Date]>(
    (state) => state.patient.dates,
  );

  const { hasClinicalRole, isPractice } = useUser();

  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const { patientInfo } = usePatientInfo({ patientGuid });

  const { updateInteractionStatus } = useUpdateInteractionStatus();

  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });

  const handleCancelForm = () => {
    setGeneratedComment(undefined);
    onClose();
  };

  const handleGenerateComment = async (values: ValuesBulkCommentGenerate) => {
    const postData = {
      startTime,
      endTime,
      timezoneOffset: new Date(startTime).getTimezoneOffset(),
    };

    setCommentLoading(true);

    const { error, data } = await dataService.createOne(
      API_READINGS_BULK_COMMENT_GENERATE(patientGuid),
      postData,
    );

    setCommentLoading(false);

    if (!error) {
      let { comment } = data.result;
      if (values.comment) {
        comment += `\n\n${values.comment}`;
      }
      setGeneratedComment(comment);
      setSubmitError(null);
    } else {
      setSubmitError(error);
    }
  };

  const handleSubmit = async (values: ValuesBulkCommentSubmit) => {
    const confirmRes = await showAlert({
      title: t('confirm.bulkReviewReadings'),
      messageTitle: t('confirm.bulkReviewSave', { value: activityCount }),
      successBtnLabel: t('controls.yes'),
      rejectBtnLabel: t('controls.no'),
    });
    if (!confirmRes) {
      return;
    }

    const postData = {
      ...values,
      startTime,
      endTime,
    };
    setReviewLoading(true);

    const { error } = await dataService.createOne(
      API_READINGS_BULK_COMMENT_SUBMIT,
      postData,
    );

    setReviewLoading(false);

    if (error) {
      showResult(error);
    } else {
      showResult(null, common.submitSuccessTypes.bulkReviewed);
      setGeneratedComment('');
      onClose();
      if (!patientInfo?.interactionStatus && hasClinicalRole) {
        const confirm = await showMonthlyInteractionAlert({
          showSecondAlert: isPractice,
        });
        if (confirm) {
          await updateInteractionStatus({
            patientGuid,
            isInteracted: true,
          });
        }
      }
    }
  };

  return {
    MonthlyInteractionAlert,
    Alert,
    generatedComment,
    submitError,
    onSubmit: handleSubmit,
    onGenerateComment: handleGenerateComment,
    onCancelForm: handleCancelForm,
    setGeneratedComment,
    commentLoading,
    reviewLoading,
  };
};
